import { uploadLogs } from '../api/logs';
import { logRepo } from '../db/logRepo';
import { prefs } from '../db/prefs';
import { contentRepo } from '../db/contentRepo';
import type { ContentRow, LogRow } from '../types/content';
import type { LogItem } from '../types/api';
import { toIsoUtc, nowMs } from '../helpers/datetime';

const LOG_FLUSH_INTERVAL_MS = 30 * 60 * 1000;

let flushing = false;

// Record one completed play. Mirrors LogManager.add().
export async function recordPlay(content: ContentRow, startMs: number, endMs: number): Promise<void> {
  const row: LogRow = {
    ad: content.logAd,
    device: content.logDevice,
    location: content.logLocation,
    subscription: content.logSubscription,
    startMs,
    endMs,
    duration: content.duration,
  };
  await logRepo.add(row);
}

function toLogItem(row: LogRow): LogItem {
  return {
    ad_id: row.ad,
    subscription_id: row.subscription,
    device_id: row.device,
    location_id: row.location,
    started_at: toIsoUtc(row.startMs),
    ended_at: toIsoUtc(row.endMs),
    duration: row.duration,
  };
}

// Upload queued logs and delete them on success. Mirrors LogManager.start().
export async function flushLogs(): Promise<void> {
  if (flushing || !navigator.onLine) return;
  flushing = true;
  try {
    const batch = await logRepo.getBatch(1000);
    if (batch.length === 0) return;

    await uploadLogs(batch.map(toLogItem));

    const ids = batch.map((r) => r.id).filter((id): id is number => id !== undefined);
    await logRepo.deleteMany(ids);
    await prefs.setLastLogTs(nowMs());
  } catch {
    // Keep logs queued for the next attempt.
  } finally {
    flushing = false;
  }
}

// Periodic check: flush when 30 min elapsed since last successful upload.
export async function checkLogs(): Promise<void> {
  const last = await prefs.getLastLogTs();
  if (nowMs() - last >= LOG_FLUSH_INTERVAL_MS) {
    await flushLogs();
  }
}

// Re-export for callers that need to know whether the device is configured.
export async function hasContent(): Promise<boolean> {
  return (await contentRepo.count()) > 0;
}
